/**
 * The Add Car flow, wired to `POST /garages/:id/cars` (owner only).
 *
 * The form's shape is the mock's, unchanged — the live preview, the drivetrain
 * pickers, the "Enter your own" escape hatches. What is new is the seam at the
 * bottom: `toRequest` turns this form's strings into `CreateCarRequest`, which
 * is the contract's shape, and every field it produces is one the API parses.
 *
 * Validation is deliberately thin here. The contract already states every rule
 * (`nickname` 1–60, `year` 1900–2100, `odometer` ≤ 10,000,000) and the API
 * enforces them, returning per-field `issues`. Re-implementing those checks in
 * the browser would give two sets of rules to keep in step, so instead the API's
 * verdict is rendered against the field it names.
 */

import { useState } from 'react';
import { Car, Trash2 } from 'lucide-react';

import type { CarDto, CreateCarRequest } from '@svcdue/contracts';

import { useCreateCar, useDeleteCar, useUpdateCar } from '../api/hooks';
import { ApiError } from '../api/client';
import { errorMessage } from '../components/states';
import { Field, Opts, SheetHead } from '../components/form';

const ADDER_OPTS = ['Naturally aspirated', 'Turbo', 'Supercharger', 'Nitrous'];
const TRANS_OPTS = ['Powerglide', 'TH350', 'TH400', '4L80E', 'Lenco', 'Manual (clutch)', 'Planetary (dragster)', 'Enter your own'];
const CLASS_OPTS = ['Bracket / ET', 'Super Street', '10.5 Outlaw', 'Top Sportsman', 'Top Dragster', 'No Prep', 'Pro Mod', 'Stick Shift', 'Unlimited', 'Enter your own'];

const CUSTOM = 'Enter your own';

interface CarForm {
  nick: string;
  year: string;
  make: string;
  model: string;
  motor: string;
  adder: string;
  trans: string;
  transOther: string;
  cls: string;
  clsOther: string;
  use: 'street' | 'strip';
  odo: string;
}

const EMPTY: CarForm = {
  nick: '',
  year: '',
  make: '',
  model: '',
  motor: '',
  adder: 'Turbo',
  trans: 'TH400',
  transOther: '',
  cls: 'Bracket / ET',
  clsOther: '',
  use: 'strip',
  odo: '',
};

/**
 * Form strings → the contract's request.
 *
 * Exported for the tests: this is where a typo silently becomes a bad request,
 * and it is pure, so it can be checked without rendering anything.
 *
 * Two rules it encodes:
 *  - **Empty means absent, not empty-string.** The contract's `optionalText`
 *    transforms `''` to `null` anyway, but sending `undefined` keeps the request
 *    honest about what the user actually filled in.
 *  - **Numbers are parsed, never coerced blindly.** `Number('')` is `0`, which
 *    would enter a car with a year of 0 rather than no year at all.
 */
export function toRequest(form: CarForm): CreateCarRequest {
  const text = (value: string): string | undefined => (value.trim() ? value.trim() : undefined);
  const digits = (value: string): number | undefined => {
    // Racers type "68,420" — strip the separators before parsing.
    const parsed = Number(value.replace(/[,\s]/g, ''));
    return value.trim() && Number.isFinite(parsed) ? parsed : undefined;
  };

  return {
    nickname: form.nick.trim(),
    year: digits(form.year) ?? null,
    make: text(form.make) ?? null,
    model: text(form.model) ?? null,
    motorCode: text(form.motor) ?? null,
    powerAdder: text(form.adder) ?? null,
    transmission: (form.trans === CUSTOM ? text(form.transOther) : text(form.trans)) ?? null,
    raceClass: (form.cls === CUSTOM ? text(form.clsOther) : text(form.cls)) ?? null,
    useType: form.use,
    // A strip-only car has no odometer to record (§2) — sending one would be
    // recording a number the app then never shows.
    odometer: form.use === 'street' ? (digits(form.odo) ?? null) : null,
  };
}

/**
 * A stored car → the form.
 *
 * The reverse of `toRequest`, for the edit path. The one subtlety is the
 * free-text pickers: a transmission or class the user typed via "Enter your own"
 * is not in the fixed option list, so it has to reopen the escape hatch —
 * anything not in the list selects CUSTOM and drops the value into the "other"
 * field, rather than silently losing it.
 */
export function carToForm(car: CarDto): CarForm {
  const pick = (value: string | null, options: string[]) =>
    value && options.includes(value)
      ? { primary: value, other: '' }
      : { primary: value ? CUSTOM : options[0]!, other: value ?? '' };

  const trans = pick(car.transmission, TRANS_OPTS);
  const cls = pick(car.raceClass, CLASS_OPTS);

  return {
    nick: car.nickname,
    year: car.year?.toString() ?? '',
    make: car.make ?? '',
    model: car.model ?? '',
    motor: car.motorCode ?? '',
    // Power adder is a fixed set; fall back to the default if it is somehow off-list.
    adder: car.powerAdder && ADDER_OPTS.includes(car.powerAdder) ? car.powerAdder : EMPTY.adder,
    trans: trans.primary,
    transOther: trans.other,
    cls: cls.primary,
    clsOther: cls.other,
    use: car.useType,
    odo: car.odometer?.toLocaleString('en-US') ?? '',
  };
}

/**
 * One flow, two jobs: add a car, or edit one.
 *
 * They share every field, the live preview, and `toRequest` — the only
 * differences are which mutation runs, the labels, and that editing can also
 * delete. Splitting them into two near-identical sheets would be two things to
 * keep in step for no gain.
 */
export function AddCar({ garageId, car, onClose }: { garageId: string; car?: CarDto; onClose: () => void }) {
  const editing = car !== undefined;
  const [c, setC] = useState<CarForm>(car ? carToForm(car) : EMPTY);
  const create = useCreateCar(garageId);
  // Hooks are unconditional; the unused one for the current mode simply never fires.
  const update = useUpdateCar(garageId, car?.id ?? '');
  const del = useDeleteCar(garageId);
  const set = <K extends keyof CarForm>(k: K, v: CarForm[K]) => setC((s) => ({ ...s, [k]: v }));

  const save = editing ? update : create;
  const [confirmDelete, setConfirmDelete] = useState(false);

  const title = c.nick || 'New car';
  const idLine = [c.year, c.make, c.model].filter(Boolean).join(' ') || 'Year · Make · Model';
  const transDisplay = c.trans === CUSTOM ? c.transOther || 'Custom trans' : c.trans;
  const specLine = [c.motor, c.adder, transDisplay].filter(Boolean).join(' · ');

  const issue = (path: string) => (save.error instanceof ApiError ? save.error.issueFor(path) : undefined);
  const busy = save.isPending || del.isPending;
  const submit = () => {
    if (!c.nick.trim() || busy) return;
    save.mutate(toRequest(c), { onSuccess: onClose });
  };
  const remove = () => {
    if (!car || busy) return;
    del.mutate(car.id, { onSuccess: onClose });
  };

  return (
    <div className="sheet">
      <SheetHead
        title={editing ? 'Edit car' : 'Add car'}
        onClose={onClose}
        saveLabel={save.isPending ? 'Saving…' : editing ? 'Save' : 'Add car'}
        onSave={submit}
        saveDisabled={busy}
      />
      <div className="sheet-body">
        <div className="preview-card">
          <div className="car-ic">
            <Car size={18} />
          </div>
          <div className="car-id">
            <b>{title}</b>
            <span>
              {idLine}
              {specLine ? ' · ' + specLine : ''}
            </span>
          </div>
          <span className={'use-chip ' + (c.use === 'street' ? 'use-street' : 'use-strip')}>
            {c.use === 'street' ? 'Street + Strip' : 'Strip only'}
          </span>
        </div>

        <div className="form-sec">Identity</div>
        <Field label="Nickname">
          <input className="input" value={c.nick} onChange={(e) => set('nick', e.target.value)} placeholder="e.g., Street Fox" />
          {issue('nickname') && <div className="field-err">{issue('nickname')}</div>}
        </Field>
        <div className="field-grid-3">
          <Field label="Year">
            <input className="input" value={c.year} onChange={(e) => set('year', e.target.value)} placeholder="1989" inputMode="numeric" />
            {issue('year') && <div className="field-err">{issue('year')}</div>}
          </Field>
          <Field label="Make">
            <input className="input" value={c.make} onChange={(e) => set('make', e.target.value)} placeholder="Ford" />
          </Field>
          <Field label="Model">
            <input className="input" value={c.model} onChange={(e) => set('model', e.target.value)} placeholder="Mustang GT" />
          </Field>
        </div>

        <div className="form-sec">Drivetrain</div>
        <Field label="Motor code">
          <input className="input" value={c.motor} onChange={(e) => set('motor', e.target.value)} placeholder="e.g., LS3 376 · 426 Hemi · Coyote 5.0" />
        </Field>
        <Field label="Power adder">
          <Opts options={ADDER_OPTS} value={c.adder} onChange={(v) => set('adder', v)} />
        </Field>
        <Field label="Transmission">
          <select className="select" value={c.trans} onChange={(e) => set('trans', e.target.value)}>
            {TRANS_OPTS.map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        </Field>
        {c.trans === CUSTOM && (
          <Field label="Your transmission">
            <input className="input" value={c.transOther} onChange={(e) => set('transOther', e.target.value)} placeholder="e.g., Liberty 5-speed · Bruno + Lenco" autoFocus />
          </Field>
        )}

        <div className="form-sec">Racing</div>
        <Field label="Class">
          <select className="select" value={c.cls} onChange={(e) => set('cls', e.target.value)}>
            {CLASS_OPTS.map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        </Field>
        {c.cls === CUSTOM && (
          <Field label="Your class">
            <input className="input" value={c.clsOther} onChange={(e) => set('clsOther', e.target.value)} placeholder="e.g., X275 · Limited Drag Radial · Outlaw 632" autoFocus />
          </Field>
        )}
        <Field label="Use">
          <div className="opts">
            <button type="button" className={'opt' + (c.use === 'street' ? ' opt-on' : '')} onClick={() => set('use', 'street')}>
              Street + Strip
            </button>
            <button type="button" className={'opt' + (c.use === 'strip' ? ' opt-on' : '')} onClick={() => set('use', 'strip')}>
              Strip only
            </button>
          </div>
        </Field>
        {c.use === 'street' && (
          <Field label="Current odometer" hint="street-driven — enables mileage triggers">
            <div className="input-suffix">
              <input className="input" value={c.odo} onChange={(e) => set('odo', e.target.value)} placeholder="68,420" inputMode="numeric" />
              <span>mi</span>
            </div>
          </Field>
        )}

        {save.isError && <div className="field-err">{errorMessage(save.error)}</div>}

        <button className="sheet-save-lg" onClick={submit} disabled={!c.nick.trim() || busy}>
          {save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Add car to garage'}
        </button>

        {/* Delete lives only in the edit sheet, and behind a confirming second
            tap: it cascades, taking the car's parts and events with it, so it is
            not a thing to do by a stray click. */}
        {editing && (
          <div className="danger-zone">
            {del.isError && <div className="field-err">{errorMessage(del.error)}</div>}
            {confirmDelete ? (
              <div className="danger-confirm">
                <span>Delete {car.nickname}? This also removes its parts and events.</span>
                <div className="danger-actions">
                  <button className="act" onClick={() => setConfirmDelete(false)} disabled={busy}>
                    Keep
                  </button>
                  <button className="act act-danger" onClick={remove} disabled={busy}>
                    {del.isPending ? 'Deleting…' : 'Delete car'}
                  </button>
                </div>
              </div>
            ) : (
              <button className="danger-link" onClick={() => setConfirmDelete(true)} disabled={busy}>
                <Trash2 size={13} /> Delete this car
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
